import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createCheckout } from '../services/api';

const ALL_PLANS = [
  {
    key: 'essential',
    name: 'Essential',
    price_mo: 349,
    price_an: 279,
    desc: 'For small staffing firms quoting across a handful of countries.',
    features: [
      { label: '50 quotations per month',         included: true,  bold: true },
      { label: '3 user seats',                    included: true,  bold: true },
      { label: '3 countries active',              included: true,  bold: true },
      { label: '30-day quote history',            included: true,  bold: true },
      { label: 'Actual Tax engine',               included: true  },
      { label: 'PDF & Excel export',              included: true  },
      { label: 'Country comparison grid',         included: false },
      { label: 'Bulk & comparison export',        included: false },
    ],
    overage: '$8 per 10 extra quotations',
  },
  {
    key: 'standard',
    name: 'Standard',
    price_mo: 499,
    price_an: 399,
    desc: 'For active staffing firms quoting at volume across all 8 countries.',
    popular: true,
    features: [
      { label: '200 quotations per month',        included: true,  bold: true },
      { label: '10 user seats',                   included: true,  bold: true },
      { label: 'All 8 countries unlocked',        included: true,  bold: true },
      { label: 'Unlimited quote history',         included: true,  bold: true },
      { label: 'Actual Tax engine',               included: true  },
      { label: 'PDF & Excel export',              included: true  },
      { label: 'Country comparison grid',         included: true  },
      { label: 'Bulk & comparison export',        included: true  },
    ],
    overage: '$6 per 10 extra quotations',
  },
  {
    key: 'enterprise',
    name: 'Enterprise',
    price_mo: null,
    price_an: null,
    desc: 'For large groups with specific requirements. Quoted by our sales team.',
    features: [
      { label: 'Unlimited quotations',            included: true,  bold: true },
      { label: 'Unlimited user seats',            included: true,  bold: true },
      { label: 'All 8 countries + custom',        included: true,  bold: true },
      { label: 'Unlimited quote history',         included: true,  bold: true },
      { label: 'Everything in Standard',          included: true  },
      { label: 'Multiple admin users',            included: true  },
      { label: 'White-label branded exports',     included: true  },
      { label: 'Dedicated account manager',       included: true  },
    ],
    overage: 'No overage charges',
  },
];

export default function PlansPage() {
  const [annual,       setAnnual]       = useState(false);
  const [upgrading,    setUpgrading]    = useState(null);
  const [contactModal, setContactModal] = useState(false);
  const { org, adminEmail } = useAuth();
  const navigate = useNavigate();
  const currentPlan = org?.plan || null;

  const handleUpgrade = async (planKey, billing_cycle) => {
    setUpgrading(planKey);
    try {
      const res = await createCheckout({ plan: planKey, billing_cycle });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (e) {
      // Show contact modal instead of toast
      setContactModal(true);
    } finally { setUpgrading(null); }
  };

  return (
    <div>
      <div style={{marginBottom:24}}>
        <h1 className="page-title">Plans & Pricing</h1>
        <p className="page-sub">
          {currentPlan
            ? <>You are currently on the <strong style={{color:'var(--precision)',textTransform:'capitalize'}}>{currentPlan}</strong> plan. Contact your administrator to upgrade.</>
            : 'Choose the plan that fits your team.'}
        </p>
      </div>

      {/* Annual toggle */}
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:28}}>
        <span style={{fontSize:14,color:!annual?'var(--text)':'var(--text2)',fontWeight:!annual?500:400}}>Monthly</span>
        <div onClick={() => setAnnual(a => !a)} style={{
          width:44,height:24,borderRadius:12,background:'var(--precision)',
          cursor:'pointer',position:'relative',flexShrink:0,
        }}>
          <div style={{
            width:18,height:18,borderRadius:'50%',background:'#fff',
            position:'absolute',top:3,left:annual?23:3,
            transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.2)',
          }}/>
        </div>
        <span style={{fontSize:14,color:annual?'var(--text)':'var(--text2)',fontWeight:annual?500:400}}>
          Annual
          <span style={{background:'var(--success)',color:'#fff',fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:20,marginLeft:6}}>Save 20%</span>
        </span>
      </div>

      {/* Plan cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))',gap:16,marginBottom:28}}>
        {ALL_PLANS.map(plan => {
          const isCurrent = currentPlan === plan.key;
          return (
            <div key={plan.key} style={{
              background:'var(--surface)',
              border: isCurrent ? `2px solid var(--precision)` : plan.popular ? '2px solid var(--precision)' : '1px solid var(--border)',
              borderRadius:14,padding:'1.5rem',
              display:'flex',flexDirection:'column',
              position:'relative',
              boxShadow: plan.popular ? '0 4px 20px rgba(14,165,233,.1)' : '0 1px 4px rgba(0,0,0,.05)',
            }}>
              {/* Badges */}
              {isCurrent && (
                <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',
                  background:'var(--precision)',color:'#fff',fontSize:11,fontWeight:600,
                  padding:'3px 14px',borderRadius:20,whiteSpace:'nowrap'}}>
                  Your current plan
                </div>
              )}
              {!isCurrent && plan.popular && (
                <div style={{position:'absolute',top:-12,left:'50%',transform:'translateX(-50%)',
                  background:'var(--precision)',color:'#fff',fontSize:11,fontWeight:600,
                  padding:'3px 14px',borderRadius:20,whiteSpace:'nowrap'}}>
                  Most popular
                </div>
              )}

              <div style={{fontSize:10.5,fontWeight:600,textTransform:'uppercase',letterSpacing:'.08em',color:'var(--muted)',marginBottom:5}}>{plan.name}</div>
              <div style={{fontSize:13,color:'var(--text2)',lineHeight:1.55,marginBottom:'1rem',minHeight:36}}>{plan.desc}</div>

              {/* Price */}
              {plan.price_mo ? (
                <div style={{marginBottom:4}}>
                  <div style={{display:'flex',alignItems:'baseline',gap:4}}>
                    <span style={{fontSize:17,color:'var(--text2)',alignSelf:'flex-start',marginTop:4}}>$</span>
                    <span style={{fontSize:36,fontWeight:700,color:'var(--text)',lineHeight:1}}>{annual ? plan.price_an : plan.price_mo}</span>
                    <span style={{fontSize:13,color:'var(--muted)'}}>/month</span>
                  </div>
                  {annual && <div style={{fontSize:12,color:'var(--success)',marginTop:2}}>Billed ${plan.price_an * 12}/year</div>}
                </div>
              ) : (
                <div style={{fontSize:26,fontWeight:700,color:'var(--text)',marginBottom:4,marginTop:2}}>Talk to us</div>
              )}

              <div style={{fontSize:11.5,color:'var(--muted)',marginBottom:'1rem',minHeight:16}}>{plan.overage}</div>
              <hr style={{border:'none',borderTop:'1px solid var(--border)',margin:'.85rem 0'}}/>

              {/* Features */}
              <ul style={{listStyle:'none',padding:0,margin:'0 0 1.25rem',flex:1,display:'flex',flexDirection:'column',gap:8}}>
                {plan.features.map((f,i) => (
                  <li key={i} style={{display:'flex',alignItems:'flex-start',gap:8,fontSize:13,color:'var(--text2)',lineHeight:1.45}}>
                    <i className={`ti ti-${f.included ? 'check' : 'x'}`}
                      style={{fontSize:15,flexShrink:0,marginTop:1,color:f.included?'var(--success)':'var(--muted)'}}
                      aria-hidden="true"/>
                    <span>{f.bold ? <strong style={{color:'var(--text)',fontWeight:500}}>{f.label}</strong> : f.label}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {isCurrent ? (
                <button className="btn btn-outline" style={{width:'100%',justifyContent:'center'}} disabled>
                  <i className="ti ti-check" aria-hidden="true"/> Current plan
                </button>
              ) : plan.key === 'enterprise' ? (
                <button className="btn btn-outline" style={{width:'100%',justifyContent:'center'}}
                  onClick={() => window.open('mailto:sales@quoteology.com?subject=Enterprise enquiry','_blank')}>
                  <i className="ti ti-mail" aria-hidden="true"/> Contact sales
                </button>
              ) : (
                <button className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'}`}
                  style={{width:'100%',justifyContent:'center'}}
                  disabled={upgrading === plan.key}
                  onClick={() => handleUpgrade(plan.key, annual ? 'annual' : 'monthly')}>
                  {upgrading === plan.key
                    ? <><div className="spinner"/>Redirecting to payment…</>
                    : <><i className="ti ti-credit-card" aria-hidden="true"/> Upgrade to {plan.name}</>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Comparison table */}
      <div className="card">
        <div className="section-header">
          <div className="section-header-dot"/>
          <span style={{fontWeight:600,fontSize:13.5}}>Full plan comparison</span>
        </div>
        <div style={{overflowX:'auto'}}>
          <table className="table" style={{minWidth:520}}>
            <thead>
              <tr><th style={{width:'40%'}}></th><th>Essential</th><th>Standard</th><th>Enterprise</th></tr>
            </thead>
            <tbody>
              {[
                ['Monthly price',            '$349',      '$499',       'Custom'],
                ['Annual price',             '$279/mo',   '$399/mo',    'Custom'],
                ['Quotations included',      '50/month',  '200/month',  'Unlimited'],
                ['Overage rate',             '$8 per 10', '$6 per 10',  'None'],
                ['User seats',               '3',         '10',         'Unlimited'],
                ['Countries',                '3 of 8',    'All 8',      'All 8 + custom'],
                ['Quote history',            '30 days',   'Unlimited',  'Unlimited'],
              ].map(([label,...vals]) => (
                <tr key={label}><td style={{fontWeight:500}}>{label}</td>{vals.map((v,i)=><td key={i}>{v}</td>)}</tr>
              ))}
              {[
                ['Actual Tax engine',         true,  true,  true ],
                ['PDF / Excel export',        true,  true,  true ],
                ['Country comparison grid',   false, true,  true ],
                ['Bulk & comparison export',  false, true,  true ],
                ['White-label exports',       false, false, true ],
                ['Dedicated account manager', false, false, true ],
              ].map(([label,...vals]) => (
                <tr key={label}>
                  <td style={{fontWeight:500}}>{label}</td>
                  {vals.map((v,i) => (
                    <td key={i}>
                      {v
                        ? <i className="ti ti-check" style={{color:'var(--success)',fontSize:15}} aria-hidden="true"/>
                        : <i className="ti ti-x"     style={{color:'var(--muted)',  fontSize:15}} aria-hidden="true"/>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Contact administrator modal */}
      {contactModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(2px)'}}>
          <div style={{background:'var(--surface)',borderRadius:16,width:'100%',maxWidth:420,boxShadow:'0 24px 64px rgba(0,0,0,.18)',border:'1px solid var(--border)',overflow:'hidden'}}>
            <div style={{background:'#0C1220',padding:'20px 24px',display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:'rgba(14,165,233,.2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <i className="ti ti-mail" style={{fontSize:20,color:'#38BDF8'}} aria-hidden="true"/>
              </div>
              <div>
                <div style={{color:'#fff',fontWeight:700,fontSize:15}}>Contact your administrator</div>
                <div style={{color:'rgba(255,255,255,.45)',fontSize:12,marginTop:2}}>Payment not yet configured</div>
              </div>
            </div>
            <div style={{padding:'20px 24px'}}>
              <p style={{fontSize:13.5,color:'var(--text2)',lineHeight:1.7,marginBottom:20}}>
                Online payment is not configured yet for this platform. To upgrade your plan, please contact your administrator and request an upgrade.
              </p>
              <a
                href={`mailto:${adminEmail}?subject=Plan upgrade request&body=Hi,%0A%0AI would like to upgrade my Quoteology plan to Standard. Please assist.%0A%0AThank you.`}
                style={{
                  display:'flex',alignItems:'center',justifyContent:'center',gap:8,
                  width:'100%',padding:'11px',
                  background:'var(--precision)',color:'#fff',
                  borderRadius:8,fontSize:14,fontWeight:600,
                  textDecoration:'none',marginBottom:10,
                  boxSizing:'border-box',
                }}>
                <i className="ti ti-mail" aria-hidden="true"/> Email {adminEmail}
              </a>
              <button
                onClick={() => setContactModal(false)}
                style={{width:'100%',padding:'10px',background:'transparent',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text2)',cursor:'pointer',fontFamily:'inherit'}}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}